import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react'

import { Avatar, ClosingDropdown } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import LikersPopover from '../LikersPopover/LikersPopover'

// Figma caps the stack at three faces (variant `likes count=3+`).
const MAX_STACKED_LIKERS = 3

// The overlapping avatars next to the like count, and the trigger for the list of
// everyone who liked the model. Rendered only once the first page has landed, so
// the stack never occupies space it cannot fill.
function LikersStack({
  likers, totalCount, hasMore = false, isLoading = false, onLoadMore,
}) {
  const t = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [triggerCloseDropdown, setTriggerCloseDropdown] = useState(false)
  const triggerRef = useRef(null)

  // Only Escape and the header button route through here, so focus can go back to
  // the trigger unconditionally: both are deliberate dismissals, and the popover
  // it came from is about to unmount. An outside click closes through Triangle's
  // dropdown instead and never reaches this, which is what we want - it must leave
  // focus on whatever the user clicked.
  const handleClose = useCallback(() => {
    setTriggerCloseDropdown(true)
    triggerRef.current?.focus()
  }, [])

  // Triangle's dropdown closes on outside click but has no key handling, and a
  // keyboard user must be able to dismiss a non-modal popover with Escape.
  useEffect(() => {
    if (!isOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose()
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleClose])

  return (
    <ClosingDropdown
      content={(
        <LikersPopover
          likers={likers}
          totalCount={totalCount}
          hasMore={hasMore}
          isLoading={isLoading}
          onLoadMore={onLoadMore}
          onClose={handleClose}
        />
      )}
      isSimpleContent
      size="medium"
      placement="top-start"
      className="footer-actions__likers"
      contentClassName="likers-popover-wrap"
      usePortal
      onOpenChange={setIsOpen}
      triggerCloseDropdown={triggerCloseDropdown}
      setTriggerCloseDropdown={setTriggerCloseDropdown}
    >
      <button
        ref={triggerRef}
        type="button"
        className="likers-stack"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={t('likers.openList')}
      >
        {likers.slice(0, MAX_STACKED_LIKERS).map(liker => (
          <Avatar
            key={liker.id}
            className="likers-stack__avatar"
            size="24"
            img={liker.avatarUrl || ''}
            username={liker.username}
            useSingleLetter
          />
        ))}
      </button>
    </ClosingDropdown>
  )
}

LikersStack.propTypes = {
  likers: arrayOf(shape({
    id: number,
    username: string,
    avatarUrl: string,
  })).isRequired,
  totalCount: number.isRequired,
  hasMore: bool,
  isLoading: bool,
  onLoadMore: func.isRequired,
}

export default memo(LikersStack)
